import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  StatusBadge,
  ApprovalTimeline,
  ActionButtonGroup,
  ApproverRoleBadge,
  WarningBanner,
  OutstandingLoansSection,
  ClarificationThread,
  DocumentList,
  BondSelectionsSection,
  DarCaseHistorySection,
  useConfirm,
} from "./shared/components";
import { ResignationFormSections } from "./shared/ResignationFormSections";
import {
  WithdrawalOriginalSections,
  WithdrawalRequestViewSection,
} from "./shared/WithdrawalFormSections";
import {
  getResignationDetails,
  generateWithdrawalOrder,
  getApprovalHistory,
  getEmployeeDetails,
  getNoDuesFormDetails,
  submitRoleApproval,
  submitCaAction,
  respondClarification,
  getCurrentEmpNo,
  formatINR,
  formatDate,
  formatSeparationMode,
  ApprovalActionType,
  ApprovalStep,
  DarDetails,
  EmployeeDetails,
} from "./services/eSeparation.service";

// find-one responses are already run through formatDate() in the service layer
// (dd-mm-yyyy). Re-formatting that string yields an invalid date, so pass
// already-formatted values through untouched and only format raw ones.
const displayDate = (v?: string | Date | null): string => {
  if (!v) return "—";
  if (typeof v === "string" && /^\d{2}-\d{2}-\d{4}$/.test(v.trim())) return v;
  return formatDate(v);
};

// A value like "50042488" is a raw SAP code, not a human-readable name.
const isNumericCode = (v: any): boolean =>
  typeof v === "string" ? /^\d+$/.test(v.trim()) : typeof v === "number";

// Role → readable label used in the approval timeline (mirrors the app).
const ROLE_LABELS: Record<string, string> = {
  AM: "Assistant Manager",
  DY_HOD: "Dy. HOD",
  HOD: "HOD",
  DIRECTOR: "Director",
  MD: "Managing Director",
  HR: "HR",
  CA: "Competent Authority",
  // V2: the 4-level HR roster — distinct from the org-hierarchy DY_HOD/HOD
  // above, even though two of them share a job title.
  HR_SR_CRA: "HR Assistant",
  HR_AM_MANAGER: "AM / Manager (HR)",
  HR_DY_HOD: "Dy. HOD (HR)",
  HR_HOD: "HOD HR",
};

// Normalise any backend/display role variant to a canonical code.
const normalizeRoleCode = (r?: string): string => {
  const up = String(r || "").toUpperCase().trim();
  const ALIASES: Record<string, string> = {
    "ASSISTANT MANAGER": "AM",
    ASSISTANT_MANAGER: "AM",
    "DY. HOD": "DY_HOD",
    "DY HOD": "DY_HOD",
    "DEPUTY HOD": "DY_HOD",
    DEPUTY_HOD: "DY_HOD",
    "CONTROLLING OFFICER": "CO",
    DEPUTY: "CO",
  };
  if (ALIASES[up]) return ALIASES[up];
  return up.replace(/[\s.]+/g, "_").replace(/_+/g, "_");
};

// Read the logged-in user's role (ADMIN / SUBADMIN can always act).
function currentUserRole(): string {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(user?.role || "").toUpperCase();
  } catch {
    return "";
  }
}

const TYPE_CONFIG: Record<string, { title: string; icon: string; color: string; bg: string }> = {
  Intimation: { title: "Intimation & Resignation", icon: "ti-file-text", color: "#0d6efd", bg: "#e8f4fd" },
  ShortNotice: { title: "Short Notice Resignation", icon: "ti-clock", color: "#BA7517", bg: "#FFF3EC" },
  Withdrawal: { title: "Withdrawal of Resignation", icon: "ti-arrow-back-up", color: "#0F6E56", bg: "#E1F5EE" },
  BondWaiver: { title: "Waiver of Service Bond", icon: "ti-shield", color: "#4A1A6E", bg: "#F0E6F6" },
};

const ApprovalDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routes = all_routes;

  const state = (location.state as any) || {};
  const requestId = state.request_id || params.id || "";
  const routeRequestType = state.type || "Intimation";
  const role = state.role || "HOD";
  // Forces a pure read-only view (e.g. opening the original resignation from a
  // withdrawal, where the resignation is on hold and nothing is actionable).
  const viewOnly = !!state.viewOnly;

  const [details, setDetails] = useState<any>(null);
  const [history, setHistory] = useState<ApprovalStep[]>([]);
  const [empProfile, setEmpProfile] = useState<EmployeeDetails | null>(null);
  // A withdrawal is reviewed against the ORIGINAL resignation it cancels.
  const [origDetails, setOrigDetails] = useState<any>(null);
  const [downloadingOrder, setDownloadingOrder] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [replyingClar, setReplyingClar] = useState(false);

  // D&AR form state — pre-populated from the backend if already filled.
  const [darCasePending, setDarCasePending] = useState<"Y" | "N">("N");
  const [darCaseNo, setDarCaseNo] = useState("");
  const [darRemarksText, setDarRemarksText] = useState("");

  const { confirm, confirmDialog } = useConfirm();

  const loadData = useCallback(async () => {
    if (!requestId) return;
    const [detRes, histRes] = await Promise.all([
      getResignationDetails(requestId),
      getApprovalHistory(requestId),
    ]);
    let det = detRes.data || null;

    const reqTypeUp = String(det?.request_type ?? "").toUpperCase();
    if (det && (reqTypeUp.includes("NO_DUES") || reqTypeUp.includes("NODUES"))) {
      try {
        const ndRes = await getNoDuesFormDetails(requestId);
        const ndRecord: any = ndRes.data;
        const parentReqId =
          ndRecord?.request_id || ndRecord?.orig_request_id || "";
        if (parentReqId && parentReqId !== requestId) {
          const parentRes = await getResignationDetails(parentReqId);
          if (parentRes.data) {
            det = {
              ...parentRes.data,
              request_id: det.request_id ?? parentRes.data.request_id,
              request_type: det.request_type,
              status: det.status ?? parentRes.data.status,
              steps: det.steps ?? parentRes.data.steps,
              history: det.history ?? parentRes.data.history,
              dar_details: det.dar_details ?? parentRes.data.dar_details,
              documents: det.documents ?? parentRes.data.documents,
              nodues_details: ndRecord?.details ?? null,
            };
          }
        }
      } catch {
        /* fall back to base record */
      }
    }

    // A No-Dues-only case (termination / death / deputation / retirement /
    // transfer-out) has no resignation form or approval chain — send any entry
    // point (links, bookmarks, notifications) to its own No-Dues case screen.
    if (det?.display_status && String(det.request_type ?? "").toUpperCase() !== "NO_DUES") {
      navigate(routes.Esepration.noDuesCaseDetail.replace(":id", requestId), {
        replace: true,
        state: { request_id: requestId, role: state.role === "HR" ? "HR" : "Employee" },
      });
      return;
    }

    setDetails(det);
    setHistory(histRes.data || []);

    // A withdrawal and a short notice both build on the employee's ORIGINAL
    // resignation — load it so its form can be shown.
    const reqTypeNow = String(det?.request_type ?? "").toUpperCase();
    if ((reqTypeNow === "WITHDRAWAL" || reqTypeNow === "SHORT_NOTICE") && det?.orig_request_id) {
      const origRes = await getResignationDetails(det.orig_request_id);
      setOrigDetails(origRes.data || null);
    }

    const existingDar: DarDetails | null = (det as any)?.dar_details ?? null;
    if (existingDar) {
      setDarCasePending(existingDar.dar_case_pending === "Y" ? "Y" : "N");
      setDarCaseNo(existingDar.dar_case_no || "");
      setDarRemarksText(existingDar.dar_remarks || "");
    }

    const empNo = det?.emp_no || state.emp_no;
    if (empNo) {
      const profRes = await getEmployeeDetails(empNo);
      setEmpProfile(profRes.data || null);
    }
  }, [requestId, state.emp_no]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const requestType = details
    ? String(details.request_type ?? "").toUpperCase() === "SHORT_NOTICE"
      ? "ShortNotice"
      : String(details.request_type ?? "").toUpperCase() === "WITHDRAWAL"
        ? "Withdrawal"
        : "Intimation"
    : routeRequestType;

  const typeConfig = TYPE_CONFIG[requestType] || TYPE_CONFIG.Intimation;

  // A short notice is shown exactly like the original resignation form: every
  // detail comes from the ORIGINAL resignation, and only the short-notice
  // specifics (new relieving date, shortfall, amount) come from the short notice.
  const formRecord = useMemo(() => {
    if (requestType !== "ShortNotice" || !origDetails) return details || {};
    return {
      ...origDetails,
      early_relieve: "Y",
      desired_relieve_date: details?.desired_relieve_date,
      shortfall_days: details?.shortfall_days,
      short_notice_amount: details?.short_notice_amount,
    };
  }, [requestType, origDetails, details]);

  // Intimation/Resignation requests render the 7-section ResignationFormSections
  // block (occupying badge numbers 01-07) instead of the old Section 01-05;
  // Short Notice / Withdrawal keep the old 5 sections. The sections after this
  // point are shared by every request type, so their badge numbers shift
  // depending on how many sections preceded them.
  const sectionOffset = requestType === "Intimation" || requestType === "ShortNotice" ? 7 : requestType === "Withdrawal" ? 3 : 5;
  const sectionNo = (n: number) => String(sectionOffset + n).padStart(2, "0");

  const emp = useMemo(() => {
    const ed = details?.employeeDetails || {};
    const fullName =
      details?.emp_name ||
      empProfile?.emp_name ||
      [ed.firstname, ed.secondname, ed.lsastname].filter(Boolean).join(" ").trim() ||
      ed.fullName ||
      ed.emp_name ||
      "—";

    const pick = (recordVal: any, profileVal: any, edVals: any[] = []) => {
      if (profileVal && !isNumericCode(profileVal)) return profileVal;
      if (recordVal && !isNumericCode(recordVal)) return recordVal;
      for (const v of edVals) if (v && !isNumericCode(v)) return v;
      return profileVal || recordVal || edVals.find(Boolean) || "—";
    };
    return {
      emp_name: fullName || "—",
      emp_no: details?.emp_no || empProfile?.emp_no || ed.empId || state.emp_no || "—",
      designation: pick(details?.designation_text || details?.designation, empProfile?.designation, [
        ed.designation_name,
        ed.designation,
      ]),
      emp_type:
        details?.emp_type ||
        empProfile?.emp_type ||
        ed.employmentType ||
        ed.appointmentType ||
        ed.emp_type ||
        "—",
      posting: pick(details?.posting, empProfile?.posting, [
        ed.department_name,
        ed.department,
      ]),
      wing: pick(details?.wing, empProfile?.wing, [
        ed.wing_name,
        ed.wing,
      ]),
      place_of_posting: details?.place_of_posting || empProfile?.place_of_posting || ed.place_of_posting || "—",
      pay_scale:
        details?.pay_scale || empProfile?.pay_scale || ed.pay_scale || ed.payscale || "—",
      doj: displayDate(
        details?.doj ||
        empProfile?.doj ||
        ed.date_of_joining ||
        ed.dateOfJoining ||
        ed.doj
      ),
      mobile: details?.mobile || empProfile?.mobile || ed.phone || ed.mobile || "—",
      email: details?.email || empProfile?.email || ed.email || "—",
    };
  }, [details, empProfile, state]);

  const req = useMemo(
    () => ({
      intimation_date: displayDate(details?.intimation_date),
      notice_end_date: displayDate(details?.notice_end_date),
      reason: details?.reason || details?.remarks || "—",
      bond_status: details?.is_bond === "Y" || details?.is_bond === true ? "Yes" : "No",
      is_bond: details?.is_bond === "Y" || details?.is_bond === true,
      bond_type_name: details?.bond_type_name || "—",
      bond_details: details?.bond_details || "",
      training_bond_amount: Number(details?.training_bond_amount) || 0,
      early_relieving: details?.early_relieve === "Y" ? "Yes" : "No",
      desired_relieve_date: displayDate(
        details?.desired_relieve_date ||
        details?.requested_relieve_date ||
        details?.relieving_date ||
        details?.approved_wef
      ),
      shortfall_days: details?.shortfall_days || 0,
      short_notice_amount: Number(details?.short_notice_amount) || 0,
      custody_items: Array.isArray(details?.items_in_custody)
        ? details.items_in_custody
        : details?.items_in_custody
          ? String(details.items_in_custody).split(",").map((s: string) => s.trim()).filter(Boolean)
          : [],
      withdrawal_reason: details?.withdrawal_reason || "—",
    }),
    [details]
  );

  const requestStatus = String(details?.status ?? "").toUpperCase();
  const isTerminal = ["APPROVED", "REJECTED", "WITHDRAWN"].includes(requestStatus);

  const loggedInEmpId = String(getCurrentEmpNo()).trim().padStart(8, "0");
  const userRole = currentUserRole();
  const isAdmin = userRole === "ADMIN" || userRole === "SUBADMIN";
  const currentStep = useMemo(
    () =>
      (Array.isArray(details?.steps) ? details.steps : []).find(
        (s: any) => String(s?.status ?? "").toUpperCase() === "CURRENT"
      ),
    [details]
  );
  const currentApproverEmpId = String(currentStep?.approver_emp_id ?? "")
    .trim()
    .padStart(8, "0");
  const isCurrentApprover =
    !!currentStep && currentApproverEmpId === loggedInEmpId;

  // V2 only: the parallel Competent Authority decision (esep_ca_approval) —
  // unlike V1, where CA was itself a step (is_ca=true) already picked up by
  // currentStep above, V2's CA has no row in details.steps at all, so it
  // needs its own detection here.
  const caApproval = details?.ca_approval || null;
  const normalizedCaApprover = String(caApproval?.approver_emp_id ?? "")
    .trim()
    .padStart(8, "0");
  const isCaOnlyDecision =
    !currentStep &&
    caApproval?.status === "PENDING" &&
    normalizedCaApprover === loggedInEmpId;

  const canAct =
    role !== "Employee" &&
    !viewOnly &&
    !isTerminal &&
    (isAdmin || isCurrentApprover || isCaOnlyDecision);
  const approverRole =
    role === "Employee" ? "Employee" : currentStep?.role || (isCaOnlyDecision ? "CA" : role);

  // A withdrawal's chain runs up to the last person the resignation had
  // reached; everyone before them just forwards, and that last person gives
  // the final Approve (approving the last step finalizes the withdrawal).
  const isWithdrawalFinalStep =
    requestType === "Withdrawal" &&
    !!currentStep &&
    Array.isArray(details?.steps) &&
    Number(currentStep.level_no) ===
      Math.max(...details.steps.map((s: any) => Number(s?.level_no) || 0));

  const empGroup = String(details?.emp_group ?? "").toUpperCase();
  const isExecutive = empGroup === "A" || empGroup === "EXECUTIVE";
  const DAR_EDITABLE_ROLES: Set<string> = isExecutive
    ? new Set(["AM", "CO", "DY_HOD", "HOD"])
    : new Set(["AM", "CO", "DY_HOD"]);
  const canFillDar =
    canAct && DAR_EDITABLE_ROLES.has(normalizeRoleCode(currentStep?.role));

  const chainTimeline = useMemo<Partial<ApprovalStep>[]>(() => {
    const steps: any[] = Array.isArray(details?.steps) ? details.steps : [];
    const hist: any[] = Array.isArray(details?.history) ? details.history : [];
    const currentStep = steps.find(
      (s: any) => String(s?.status ?? "").toUpperCase() === "CURRENT"
    );
    const currentStepNo = Number(
      details?.current_step_no ?? currentStep?.level_no ?? 0
    );
    const items = [...steps]
      .filter((s: any) => s && String(s?.status ?? "").toUpperCase() !== "SKIPPED")
      .sort((a: any, b: any) => Number(a?.level_no ?? 0) - Number(b?.level_no ?? 0))
      .filter((s: any) => !currentStepNo || Number(s?.level_no ?? 0) <= currentStepNo)
      .map((s: any) => {
        const st = String(s?.status ?? "").toUpperCase();
        const status: ApprovalStep["status"] =
          st === "APPROVED"
            ? "Approved"
            : st === "REJECTED"
              ? "Rejected"
              : st === "CURRENT"
                ? "Current"
                : st === "SENT_BACK"
                  ? "Clarification"
                  : "Pending";
        const empId = String(s?.approver_emp_id ?? "").trim().padStart(8, "0");
        const h = [...hist]
          .reverse()
          .find(
            (x: any) =>
              String(x?.approver_emp_id ?? "").trim().padStart(8, "0") === empId &&
              Number(x?.level_no ?? -1) === Number(s?.level_no ?? -2)
          );
        return {
          level:
            ROLE_LABELS[String(s?.role ?? "").toUpperCase()] ||
            s?.role ||
            `Level ${s?.level_no}`,
          status,
          approver_name:
            h?.approver_name || s?.approver_name || s?.approver_emp_id || "—",
          approver_emp_no:
            s?.approver_emp_id || h?.approver_empId || h?.approver_emp_id || "",
          action_date: h?.acted_at || (status === "Current" ? "Awaiting action" : ""),
          remarks: h?.comments || "",
        };
      });

    // V2 only: the parallel Competent Authority decision has no row in
    // details.steps at all (unlike V1, where CA is itself a step) — append it
    // explicitly so the timeline still shows it. Omitted while NOT_SENT (HOD
    // HR hasn't dispatched it yet, so there's nothing to show).
    const ca = details?.ca_approval;
    if (ca && ca.status !== "NOT_SENT") {
      const caStatus: ApprovalStep["status"] =
        ca.status === "APPROVED"
          ? "Approved"
          : ca.status === "REJECTED"
            ? "Rejected"
            : "Current";
      items.push({
        level: "Competent Authority",
        status: caStatus,
        approver_name: ca.approver_emp_id || "—",
        approver_emp_no: ca.approver_emp_id || "",
        action_date: ca.acted_at || ca.sent_at || (caStatus === "Current" ? "Awaiting action" : ""),
        remarks: ca.remarks || "",
      });
    }

    return items;
  }, [details]);

  const clarifications: any[] = Array.isArray(details?.clarifications)
    ? details.clarifications
    : [];
  const isEmployeeView = role === "Employee";

  const visibleClarifications = clarifications.filter((c: any) => {
    if (isEmployeeView || isAdmin) return true;
    const raisedBy = String(c?.raised_by ?? "").trim().padStart(8, "0");
    return raisedBy === loggedInEmpId;
  });
  const openClarification = [...visibleClarifications]
    .reverse()
    .find((c: any) => !c.response && !c.resolved);

  const handleClarificationReply = async (
    clarificationId: number,
    text: string
  ) => {
    setReplyingClar(true);
    const res = await respondClarification({
      request_id: requestId,
      clarification_id: clarificationId,
      response_text: text,
    });
    setReplyingClar(false);
    if (res.success) {
      toast.success("Your reply has been sent to the approver.");
      await loadData();
    } else {
      toast.error(res.message || "Failed to send your reply. Please try again.");
    }
  };

  const REMARKS_MAX = 500;

  const handleAction = async (action: ApprovalActionType) => {
    if (!remarks.trim()) {
      toast.warning("Please provide remarks before proceeding.");
      return;
    }
    if (remarks.trim().length > REMARKS_MAX) {
      toast.warning(`Remarks cannot exceed ${REMARKS_MAX} characters.`);
      return;
    }

    if (canFillDar && darCasePending === "Y" && !darCaseNo.trim()) {
      toast.warning("Please enter the D&AR Case Reference No. when a case is pending.");
      return;
    }

    if (canFillDar && darCasePending === "Y" && !darRemarksText.trim()) {
      toast.warning("Please provide D&AR remarks — mandatory when a case is pending.");
      return;
    }

    const isCA = String(approverRole || "").toUpperCase() === "CA" ||
      String(approverRole || "") === "Competent Authority" ||
      isWithdrawalFinalStep;
    const approveActionLabel = isCA ? "Approve" : "Forward";

    const displayMap: Record<ApprovalActionType, string> = {
      Approve: approveActionLabel,
      Reject: "Reject",
      SendBack: "Send Back",
      Clarification: "Clarification",
    };
    const verbMap: Record<ApprovalActionType, string> = {
      Approve: isCA ? "approve" : "forward",
      Reject: "reject",
      SendBack: "send back",
      Clarification: "seek clarification on",
    };
    const successMap: Record<ApprovalActionType, string> = {
      Approve: isCA ? "approved" : "forwarded",
      Reject: "rejected",
      SendBack: "sent back",
      Clarification: "clarification requested",
    };
    const ok = await confirm(
      `Are you sure you want to ${verbMap[action]} this ${typeConfig.title} request?`,
      {
        title: `Confirm ${displayMap[action]}`,
        confirmLabel: displayMap[action],
        tone: action === "Approve" ? "success" : action === "Reject" ? "danger" : "primary",
      }
    );
    if (!ok) return;
    setLoading(true);
    // V2 parallel CA decision (esep_ca_approval) — a separate action entirely,
    // not a step in the sequential chain, so it goes through its own
    // endpoint. Only Approve/Reject are meaningful here; the Clarification
    // button is hidden for this case (see ActionButtonGroup below).
    const res = isCaOnlyDecision
      ? await submitCaAction({
        request_id: requestId,
        action: action === "Approve" ? "APPROVE" : "REJECT",
        comments: remarks.trim(),
      })
      : await submitRoleApproval({
        role,
        request_id: requestId,
        action,
        remarks: remarks.trim(),
        dar_details: canFillDar
          ? {
            dar_case_pending: darCasePending,
            dar_case_no: darCasePending === "Y" ? darCaseNo.trim() : null,
            dar_remarks: darRemarksText.trim() || null,
          }
          : null,
      });
    setLoading(false);
    if (res.success) {
      toast.success(`Request has been ${successMap[action]} successfully.`);
      navigate(routes.Esepration.approvals, { state: { role } });
    } else {
      toast.error(res.message || "Failed to process action. Please try again.");
    }
  };

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* PAGE HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto">
            <h3 className="page-title fw-bold text-dark mb-1">
              Review Request
            </h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.Esepration.hub} className="text-decoration-none text-muted">
                    E-Separation
                  </Link>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  Review Request
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <ApproverRoleBadge role={approverRole} />
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2 px-3"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left"></i> Back
            </button>
          </div>
        </div>

        {/* Request Type Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 44, height: 44, borderRadius: 10, background: typeConfig.bg, color: typeConfig.color }}
                >
                  <i className={`ti ${typeConfig.icon}`} style={{ fontSize: 22 }} />
                </span>
                <div>
                  <div
                    style={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "#344054",
                      lineHeight: "1.3",
                    }}
                  >
                    {typeConfig.title}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#667085",
                      lineHeight: "1.4",
                      marginTop: "2px",
                    }}
                  >
                    {canAct
                      ? "Review and take action on this request"
                      : "View the details and status of this request"}
                  </div>
                </div>
              </div>
              <div>
                <StatusBadge status={details?.status || "In Review"} />
              </div>
            </div>
          </div>
        </div>

        {/* Resignation ("Intimation") requests show the exact same 7 sections,
            labels and values the employee filled in on their own form — read
            only here, since every approver (including the CA) is a viewer,
            not an editor. Short Notice / Withdrawal keep their own smaller,
            request-type-specific layout below (out of scope for this parity
            requirement — they aren't the 7-section resignation form). */}
        {requestType === "Intimation" || requestType === "ShortNotice" ? (
          <>
            {requestType === "ShortNotice" && (
              <>
                <WarningBanner
                  type="info"
                  message="The employee had already resigned and now requests earlier relieving. Every detail below is from the original resignation; only the new relieving date, shortfall and short notice amount are new. The original resignation is on hold, and this short notice runs its own complete flow — the full approval hierarchy, its own Competent Authority approval and its own No-Dues."
                />
                {details?.orig_request_id && (
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() =>
                        navigate(routes.Esepration.approvalDetail.replace(":id", details.orig_request_id), {
                          state: {
                            request_id: details.orig_request_id,
                            type: "Intimation",
                            role: "Employee",
                            viewOnly: true,
                          },
                        })
                      }
                    >
                      <i className="ti ti-file-text me-1" />
                      View Original Resignation
                    </button>
                  </div>
                )}
              </>
            )}
            <ResignationFormSections d={formRecord} mode="view" empMaster={empProfile} />
          </>
        ) : requestType === "Withdrawal" ? (
          // Same sections, labels and look as the employee's own withdrawal
          // form, plus the link to the original resignation's full form.
          <>
            <WithdrawalOriginalSections
              rec={origDetails || {}}
              emp={empProfile}
              bannerMessage="These details are auto-fetched from the employee's original resignation request."
              onViewComplete={() =>
                navigate(routes.Esepration.approvalDetail.replace(":id", details?.orig_request_id), {
                  state: {
                    request_id: details?.orig_request_id,
                    type: "Intimation",
                    role: "Employee",
                    viewOnly: true,
                  },
                })
              }
            />
            <WithdrawalRequestViewSection
              withdrawalDate={details?.withdrawal_date}
              reason={details?.withdrawal_reason}
            />
            <WarningBanner
              type="info"
              message="If approved by the last approver in the chain, the original resignation will be cancelled and the employee will continue service. If rejected, the resignation resumes from where it was put on hold."
            />
            {String(details?.status ?? "").toUpperCase() === "APPROVED" && (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  disabled={downloadingOrder}
                  onClick={async () => {
                    setDownloadingOrder(true);
                    const r = await generateWithdrawalOrder(details.request_id);
                    setDownloadingOrder(false);
                    if (r.success) toast.success("Withdrawal Order downloaded.");
                    else toast.error(r.message || "Failed to download the Withdrawal Order.");
                  }}
                >
                  <i className="ti ti-file-certificate me-1" />
                  {downloadingOrder ? "Preparing…" : "Download Withdrawal Order"}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* SECTION 01: EMPLOYEE DETAILS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    01
                  </span>
                  Employee Details
                </h5>
                <StatusBadge status={details?.status || "In Review"} />
              </div>
              <div className="card-body p-4">
                <div
                  className="alert alert-info border-0 d-flex align-items-center gap-2 mb-4 py-2 px-3 rounded-3"
                  style={{ background: "#e8f4fd", color: "#0c5460" }}
                >
                  <i className="ti ti-info-circle fs-5 flex-shrink-0" />
                  <span className="small">
                    These details are auto-filled from SAP HR Master Data.
                  </span>
                </div>

                <div className="row g-3">
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Employee Name
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.emp_name}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Employee No.
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.emp_no}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Mode of Separation
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={formatSeparationMode(
                        details?.separation_type || details?.request_type
                      )}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.designation}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Regular / Contract
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.emp_type}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.posting}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Place of Posting
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.place_of_posting || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Wing
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.wing || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Pay Scale
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.pay_scale}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Date of Joining
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.doj}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Mobile No.
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.mobile}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Email
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={emp.email}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 02: REQUEST DETAILS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    02
                  </span>
                  Request Details
                </h5>
              </div>
              <div className="card-body p-4">
                {requestType === "Intimation" && (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-3 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Intimation Date
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={req.intimation_date}
                          readOnly
                        />
                      </div>
                      <div className="col-md-3 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Notice End Date
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={req.notice_end_date}
                          readOnly
                        />
                      </div>
                      <div className="col-md-3 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Under Bond
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={req.bond_status}
                          readOnly
                        />
                      </div>
                      <div className="col-md-3 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Early Relieving
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={req.early_relieving}
                          readOnly
                        />
                      </div>
                    </div>

                    {req.early_relieving === "Yes" && (
                      <div className="p-3 bg-light rounded-3 border mb-3">
                        <h6 className="fw-bold text-dark mb-2">Early Relieving Parameters</h6>
                        <div className="row g-3">
                          <div className="col-md-4">
                            <label className="form-label fw-semibold text-secondary small mb-1">
                              Desired Relieving Date
                            </label>
                            <input
                              type="text"
                              className="form-control bg-white"
                              value={req.desired_relieve_date}
                              readOnly
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold text-secondary small mb-1">
                              Shortfall Days
                            </label>
                            <input
                              type="text"
                              className="form-control bg-white"
                              value={req.shortfall_days ? `${req.shortfall_days} days` : "—"}
                              readOnly
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold text-secondary small mb-1">
                              Short Notice Amount
                            </label>
                            <input
                              type="text"
                              className="form-control bg-white fw-bold text-danger"
                              value={req.short_notice_amount ? formatINR(req.short_notice_amount) : "—"}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-2 p-3 rounded-3 bg-light border">
                      <div className="text-secondary fw-semibold small mb-1">Reason for Resignation</div>
                      <div className="text-dark">{req.reason}</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* SECTION 03: UPLOADED DOCUMENTS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    03
                  </span>
                  Uploaded Documents
                </h5>
              </div>
              <div className="card-body p-4">
                <DocumentList documents={details?.documents} />
              </div>
            </div>

            {/* SECTION 04: BOND DETAILS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    04
                  </span>
                  Bond Details
                </h5>
              </div>
              <div className="card-body p-4">
                {Array.isArray(details?.bond_selections) && details.bond_selections.length > 0 ? (
                  <BondSelectionsSection selections={details.bond_selections} />
                ) : req.is_bond ? (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Under Bond
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value="Yes"
                          readOnly
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Bond Type
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={req.bond_type_name}
                          readOnly
                        />
                      </div>
                      {req.training_bond_amount > 0 && (
                        <div className="col-md-4">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Training Cost
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light fw-bold text-danger"
                            value={formatINR(req.training_bond_amount)}
                            readOnly
                          />
                        </div>
                      )}
                    </div>
                    {req.bond_details && (
                      <div className="p-3 bg-light rounded-3 border">
                        <div className="text-secondary fw-semibold small mb-1">Bond Details</div>
                        <div className="text-dark">{req.bond_details}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <BondSelectionsSection selections={details?.bond_selections} />
                )}
              </div>
            </div>

            {/* SECTION 05: OUTSTANDING LOANS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    05
                  </span>
                  Outstanding Loans
                </h5>
              </div>
              <div className="card-body p-4">
                <OutstandingLoansSection
                  loans={details?.outstanding_loans || []}
                  note="Auto-fetched from the employee's SAP loan records."
                />
              </div>
            </div>
          </>
        )}

        {/* SECTION 06: D&AR CLEARANCE REMARKS */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                {sectionNo(1)}
              </span>
              D&AR Clearance Remarks
            </h5>
          </div>
          <div className="card-body p-4">
            {(details as any)?.dar_details ? (
              (() => {
                const d: DarDetails = (details as any).dar_details;
                const statusColor: Record<string, string> = {
                  CLEARED: "#0F6E56", PENDING: "#BA7517", HOLD: "#C0282C", NOT_APPLICABLE: "#888",
                };
                return (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-3 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Case Pending
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={d.dar_case_pending === "Y" ? "Yes" : "No"}
                          readOnly
                        />
                      </div>
                      {d.dar_case_pending === "Y" && (
                        <div className="col-md-3 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Case Reference No.
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={d.dar_case_no || "—"}
                            readOnly
                          />
                        </div>
                      )}
                      <div className="col-md-3 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Clearance Status
                        </label>
                        <div className="form-control bg-light">
                          <span style={{ fontWeight: 600, color: statusColor[d.dar_status || ""] || "#888" }}>
                            {d.dar_status?.replace(/_/g, " ") || "—"}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-3 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Filled By
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={`${d.filled_by_emp_id || "—"}${d.filled_by_role ? ` (${d.filled_by_role})` : ""}`}
                          readOnly
                        />
                      </div>
                      {d.filled_at && (
                        <div className="col-md-3 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Filled At
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={formatDate(d.filled_at)}
                            readOnly
                          />
                        </div>
                      )}
                    </div>
                    {d.dar_remarks && (
                      <div className="p-3 bg-light rounded-3 border">
                        <div className="text-secondary fw-semibold small mb-1">D&amp;AR Remarks</div>
                        <div className="text-dark">{d.dar_remarks}</div>
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              <p className="text-muted fst-italic mb-0">No D&AR clearance remarks available yet.</p>
            )}
          </div>
        </div>

        {/* SECTION 07: D&AR CASE HISTORY (approver/HR only — real module records) */}
        {!isEmployeeView && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  {sectionNo(2)}
                </span>
                D&amp;AR Case History
              </h5>
            </div>
            <div className="card-body p-4">
              <DarCaseHistorySection cases={(details as any)?.dar_module_cases} />
            </div>
          </div>
        )}

        {/* SECTION 08: APPROVAL HISTORY */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                {sectionNo(3)}
              </span>
              Approval History
            </h5>
          </div>
          <div className="card-body p-4">
            {!isTerminal && chainTimeline.length > 0 ? (
              <ApprovalTimeline steps={chainTimeline} />
            ) : history.length > 0 ? (
              <ApprovalTimeline steps={history} />
            ) : (
              <p className="text-muted fst-italic mb-0">No approval actions taken yet.</p>
            )}
          </div>
        </div>

        {/* SECTION 09: CLARIFICATIONS (IF VISIBLE) */}
        {(visibleClarifications.length > 0 ||
          (isEmployeeView && !!openClarification)) && (
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    {sectionNo(4)}
                  </span>
                  Clarifications
                </h5>
              </div>
              <div className="card-body p-4">
                <ClarificationThread
                  clarifications={visibleClarifications}
                  onReply={
                    isEmployeeView && openClarification
                      ? handleClarificationReply
                      : undefined
                  }
                  replying={replyingClar}
                />
              </div>
            </div>
          )}

        {/* SECTION 10: D&AR DETAILS (WHEN CAN FILL) */}
        {canFillDar && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  {sectionNo(5)}
                </span>
                D&amp;AR Details
              </h5>
            </div>
            <div className="card-body p-4">
              <WarningBanner
                type="info"
                message={`Please fill in the D&AR (Disciplinary & Administrative Records) status before forwarding. This information will be visible to higher-level approvers and HR. (${isExecutive ? "Editable up to HOD level for executive employees" : "Editable up to Dy. HOD level for non-executive employees"})`}
              />

              <div className="mb-3">
                <label className="form-label fw-semibold text-secondary small mb-2">
                  Any D&amp;AR Case Pending? <span className="text-danger">*</span>
                </label>
                <div className="d-flex gap-3">
                  {(["N", "Y"] as const).map((v) => (
                    <div key={v} className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id={`dar-pending-${v}`}
                        name="dar_case_pending"
                        value={v}
                        checked={darCasePending === v}
                        onChange={() => setDarCasePending(v)}
                      />
                      <label className="form-check-label fw-medium text-dark" htmlFor={`dar-pending-${v}`}>
                        {v === "Y" ? "Yes" : "No"}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {darCasePending === "Y" && (
                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Case Reference No. <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Enter D&AR case reference number"
                    value={darCaseNo}
                    onChange={(e) => setDarCaseNo(e.target.value)}
                  />
                </div>
              )}

              <div className="mb-1">
                <label className="form-label fw-semibold text-secondary small mb-1">
                  D&amp;AR Remarks {darCasePending === "Y" && <span className="text-danger">*</span>}
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter D&AR remarks..."
                  value={darRemarksText}
                  onChange={(e) => setDarRemarksText(e.target.value)}
                />
                {darCasePending === "Y" && (
                  <small className="text-muted d-block mt-1">
                    Mandatory since a D&amp;AR case is marked pending.
                  </small>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SECTION: YOUR ACTION */}
        {canAct && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  <i className="ti ti-gavel" />
                </span>
                Your Action
              </h5>
            </div>
            <div className="card-body p-4">
              <WarningBanner type="info" message={`You are reviewing as ${approverRole}. Your action will be recorded in the audit trail.`} />

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label fw-semibold text-secondary small mb-0">
                    Remarks / Comments <span className="text-danger">*</span>
                  </label>
                  <small className={remarks.length >= REMARKS_MAX ? "text-danger" : "text-muted"}>
                    {remarks.length}/{REMARKS_MAX}
                  </small>
                </div>
                <textarea
                  className="form-control"
                  rows={4}
                  maxLength={REMARKS_MAX}
                  placeholder="Enter your remarks (mandatory for all actions)..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value.slice(0, REMARKS_MAX))}
                />
                <span className="d-flex align-items-center gap-1 text-info small mt-1">
                  <i className="ti ti-info-circle" />
                  Note: You can enter a maximum of {REMARKS_MAX} characters.
                </span>
              </div>

              {(approverRole === "HR" || approverRole === "CA") && (
                <p className="text-muted fst-italic small mb-3">
                  As {approverRole}, you can modify the proposed relieving date if policy allows.
                </p>
              )}

              <ActionButtonGroup
                onApprove={() => handleAction("Approve")}
                onReject={() => handleAction("Reject")}
                onClarification={isCaOnlyDecision ? undefined : () => handleAction("Clarification")}
                loading={loading}
                approveLabel={
                  (String(approverRole || "").toUpperCase() === "CA" ||
                    approverRole === "Competent Authority" ||
                    isWithdrawalFinalStep)
                    ? "Approve"
                    : "Forward"
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalDetail;
